import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import { categories, getMangaByCategory } from '@/mocks/manga';
import type { MangaData } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'all';
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const mangaList: MangaData[] = getMangaByCategory(activeSlug);
  const totalPages = Math.ceil(mangaList.length / itemsPerPage);
  const paginatedManga = mangaList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSlug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  const categoryLabelMap: Record<string, string> = {
    all: '全部漫画',
    japanese: '日漫',
    korean: '韩漫',
    chinese: '国漫',
    completed: '已完结',
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `酷漫屋 - ${categoryLabelMap[activeSlug] || '分类'}`,
            description: `酷漫屋${categoryLabelMap[activeSlug] || ''}分类 - 海量免费漫画在线阅读，日漫韩漫国漫免费看`,
            url: `https://kumanwu.com/category/${activeSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '酷漫屋',
              url: 'https://kumanwu.com',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://kumanwu.com',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: categoryLabelMap[activeSlug] || '分类',
                item: `https://kumanwu.com/category/${activeSlug}`,
              },
            ],
          }),
        }}
      />

      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryLabelMap[activeSlug] || '分类' },
          ]}
        />

        {/* Category Tabs */}
        <section className="px-4 md:px-6 mb-6">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-xs`}></i>
                </span>
                {cat.label}
              </Link>
            ))}
          </div>
        </section>

        {/* Results Info */}
        <section className="px-4 md:px-6 mb-4">
          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
            {categoryLabelMap[activeSlug] || '分类'}
          </h1>
          <p className="text-sm text-foreground-500 mt-1">
            共找到 {mangaList.length} 部漫画
          </p>
        </section>

        {/* Manga Grid */}
        <section className="px-4 md:px-6 pb-10" data-product-shop>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {paginatedManga.map((manga) => (
              <MangaCard
                key={manga.id}
                id={manga.id}
                slug={manga.slug}
                title={manga.title}
                coverImage={manga.coverImage}
                status={manga.status}
                category={manga.category}
                rating={manga.rating}
                updatedAt={manga.updatedAt}
                chapterCount={manga.chapters.length}
              />
            ))}
          </div>

          {paginatedManga.length === 0 && (
            <div className="text-center py-20">
              <span className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
              </span>
              <p className="text-foreground-500">暂无漫画</p>
            </div>
          )}
        </section>

        {/* Pagination */}
        {totalPages > 1 && (
          <nav className="px-4 md:px-6 pb-14 flex items-center justify-center gap-2" aria-label="分页导航">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="上一页"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-colors cursor-pointer ${
                  page === currentPage
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-200 text-foreground-600 hover:bg-background-100'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="下一页"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </nav>
        )}
      </main>
      <Footer />
    </>
  );
}